<template>
    <q-card-section
      v-show="derate"
      class="row justify-between"
    >
    <h6 class="text-primary">Derating Values</h6>
    <div class="q-gutter-md">
     <thickness-docs />
    </div>
    </q-card-section>
  <q-card-section class="q-gutter-xs">
      <q-input
        :dark="isDarkModeGlobal"
        v-show="deratingParameters"
        filled
        dense
        for="coupling_thickness_mm"
        label="Coupling thickness [mm]"
        type="number"
        step="1"
        v-model="coupling_thickness_mm"
        :rules="rules"

        @update:modelValue="executeCalculation(coupling_thickness_mm, 'pr')"
        hide-bottom-space
      >
        <template #append>
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            :text="` Distance from transducer exit plane to skin surface.`"
          />
        </template>
      </q-input>
      <q-input
        :dark="isDarkModeGlobal"
        v-show="deratingParameters"
        filled
        dense
        for="thickness_of_scalp"
        label="Scalp thickness [mm]"
        type="number"
        step="0.000001"
        v-model="thickness_of_scalp_mm"
        :rules="rules"
        @update:modelValue="executeCalculation(thickness_of_scalp_mm, 'pr')"
        hide-bottom-space
      >
        <template #append>
          <reference-popup-proxy
            :link="'https://doi.org/10.1001/archderm.1954.01540230051006'"
          />

          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            :text="`Distance from skin surface to outer surface of skull.`"
          />
        </template>
      </q-input>
      <q-input
        :dark="isDarkModeGlobal"
        v-show="deratingParameters"
        filled
        dense
        for="thickness_of_skull"
        label="Skull thickness [mm]"
        type="number"
        step="1"
        v-model="thickness_of_skull_mm"
        :rules="rules"

        @update:modelValue="executeCalculation(thickness_of_skull_mm, 'pr')"
        hide-bottom-space
      >
        <template #append>
          <reference-popup-proxy
            icon="warning"
            color="red"
            tooltip="Disclaimer"
            text="Default is worst case assumption of thickness of skull."
          />

          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            :text="`Distance from outer to inner surface of skull.`"
          />
        </template>
      </q-input>
      <q-input
        :dark="isDarkModeGlobal"
        v-show="deratingParameters"
        outlined
        dense
        for="us_beam_diameter_on_skin_mm"
        label="US beam diameter on skin [mm]"
        type="number"
        step="1"
        readonly
        :model-value="calculatorStore.us_beam_diameter_on_skin_mm"
      >
        <template #append>
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            :text="`Diameter of ultrasound beam at the skin surface.`"
          />
        </template>
      </q-input>
      <q-input
        :dark="isDarkModeGlobal"
        v-show="deratingParameters"
        outlined
        dense
        for="us_beam_diameter_on_skull_mm"
        label="US beam diameter on skull [mm]"
        type="number"
        step="1"
        readonly
        :model-value="calculatorStore.us_beam_diameter_on_skull_mm"
      >
        <template #append>
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            :text="`Diameter of ultrasound beam at the skull surface.`"
          />
        </template>
      </q-input>
    </q-card-section>
</template>
<script setup lang="ts">
import {storeToRefs} from "pinia"
import {useCalculatorStore} from '../stores/calculator.store'
import {computed, ref} from "vue"
import ReferencePopupProxy from "./PopupProxyLiteratureReference.vue"
import ThicknessDocs from "./DialogThicknessDocs.vue"

const calculatorStore = useCalculatorStore()
const {
  focal_depth_mm,
  thickness_of_skull_mm,
  thickness_of_scalp_mm,
  coupling_thickness_mm,
  derate,
  isDarkModeGlobal,
  loadingValue
} = storeToRefs(calculatorStore)

const deratingParameters = ref(true)
const rules = [val => !!val || 'This field is required', val => val > 0 || 'Value must be > 0']
const minimumValueFocalDepth = computed(() => +coupling_thickness_mm.value + +thickness_of_scalp_mm.value + +thickness_of_skull_mm.value)

const blurActiveInput = () => {
  const activeElement = document.activeElement;
  if (activeElement && activeElement.tagName === 'INPUT') {
    activeElement.blur()
  }
};

let debounceTimeout = null // Timeout reference for debounce
let interval = null // Timeout reference for interval
const loading = ref(false) // Tracks spinner visibility

let totalTime = 3000
let intervalTime = 100 // Time between each count

function executeCalculation(val: number, calcType: string) {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
    clearInterval(interval)
    loadingValue.value = 0
  }

  loading.value = true;
  interval = setInterval(() => {
  loadingValue.value += 0.1;
    if (loadingValue.value > 3) {
      clearInterval(interval); // Stop the interval when count reaches 100
      loadingValue.value = 0
    }
  }, intervalTime);
      // Start debounce timer
  debounceTimeout = setTimeout(() => {
        // After debounce delay
    loading.value = false;
    if (val > 0 && focal_depth_mm.value >= minimumValueFocalDepth.value) {
      switch (calcType) {
        case 'acoustic_power':
          calculatorStore.updateByAcousticPower()
          break
        case 'transducer_efficiency':
          calculatorStore.updateByElectricalPowerOrTransducerEfficiency()
          break
        case 'electrical_power':
          calculatorStore.updateByElectricalPowerOrTransducerEfficiency()
          break
        case 'pr':
          calculatorStore.updateByPr()
          break
        case 'MI':
          calculatorStore.updateByMI()
          break
        case 'non_derated_MI':
          calculatorStore.updateByNonDeratedMI()
          break
        default:
          calculatorStore.updateByIsppa()
          break
      }
      calculatorStore.clearNotifications()

      blurActiveInput()
    } else {
      console.error("empty value or 0 not allowed")
    }
  }, totalTime);
}
</script>
